// File: frontend/src/pages/drafts/DraftsPage.tsx

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  EntryDraft,
  getDrafts,
  deleteDraft,
  bulkDeleteDrafts,
  updateDraft,
  publishDraft,
} from "../../services/draftService";
import { generateMatchUrlFromEntry } from "../../utils/slugGenerator";
import { Pagination } from "../../utils";
import { parseApiError } from "../../utils/parseApiError";
import {
  DRAFTS_PAGE_SIZE,
  DRAFTS_BULK_MIN,
  TOAST_DURATION_NORMAL,
} from "../../constants/settings.constants";
// Entry length rules come from the admin panel, not from a compile-time constant
import { useSiteSettings } from "../../context/SiteSettingsContext";
import {
  FaTrash,
  FaPaperPlane,
  FaRegEdit,
  FaSave,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaRegFileAlt,
} from "react-icons/fa";
import { MdKeyboard } from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";
import { ConfirmModal } from "../../components/common/ConfirmModal";
import { EntryEditorToolbar } from "../../components/entries/EntryEditorToolbar";
import { SpoilerBlock } from "../../components/entries/SpoilerBlock";
import { EntryImage, collectEntryImageUrls, normalizeImageTokenSpacing } from "../../components/entries/EntryImage";
import { renderBasicMarkup } from "../../utils/inlineMarkup";
import { findBlockToken } from "../../utils/markupUtils";
import { RateLimitError, DuplicateEntryError } from "../../services/apiClient";
import "./DraftsPage.css";
import { parseServerDate } from "../../utils/dateUtils";

// Turkish special characters — mirrors EntryPostForm
const TURKISH_CHARS = ["ç", "ğ", "ı", "ö", "ş", "ü", "Ç", "Ğ", "İ", "Ö", "Ş", "Ü"];

// ─── Preview renderer — mirrors EntryPostForm ─────────────────────────────────
const PREVIEW_MAC_REF     = /\(mac:\s*(.*?)\|\|(\d+)\)/g;
const PREVIEW_REF_NEW     = /\(ref:\s*(.*?)\|\|(\d+)\)/g;
const PREVIEW_REF_LEGACY  = /\((ref|bkz):\s*([^\s)]+)\)/g;
const PREVIEW_BOLD        = /\(b:\s*(.*?)\)/g;
const PREVIEW_ITALIC      = /\(i:\s*(.*?)\)/g;
const PREVIEW_LINK        = /\(link:\s*([^|)]+)\|([^)]+)\)/g;
const PREVIEW_ENTRY_REF   = /\(#(\d+)\)/g;
const PREVIEW_PERSON_REF  = /\(kisi:\s*(.*?)\|\|([^)]+)\)/g;
const PREVIEW_CLUB_REF    = /\(kulup:\s*(.*?)\|\|([^)]+)\)/g;
const PREVIEW_TEAM_REF    = /\(takim:\s*(.*?)\|\|([^)\s]+)\)/g;
// Custom topic reference preview: (baslik: title||slug)
const PREVIEW_TOPIC_REF   = /\(baslik:\s*(.*?)\|\|([^)]+)\)/g;
const PREVIEW_GORSEL      = /\(gorsel:\s*(https?:\/\/[^)\s]+|[a-f0-9]{32}\.(?:jpg|jpeg|png|webp))\s*\)/g;
// Spoiler/alinti block previews: (spoiler: text) / (alinti: text) — located
// with findBlockToken() (markupUtils.tsx), which mirrors EntryRow's handling.
// A single regex can't reliably find the matching closing parenthesis once
// the pasted content contains its own "(" / ")" characters.
// "(bkz: ...)" reference variants — mirror the BKZ_*_PATTERN set in EntryRow
const PREVIEW_BKZ_REFS: Array<{ pattern: RegExp; keyPrefix: string }> = [
  { pattern: /\(bkz-mac:\s*(.*?)\|\|(\d+)\)/g, keyPrefix: "bkz-mac" },
  { pattern: /\(bkz-kisi:\s*(.*?)\|\|([^)]+)\)/g, keyPrefix: "bkz-kisi" },
  { pattern: /\(bkz-kulup:\s*(.*?)\|\|([^)]+)\)/g, keyPrefix: "bkz-kulup" },
  { pattern: /\(bkz-takim:\s*(.*?)\|\|([^)\s]+)\)/g, keyPrefix: "bkz-takim" },
  { pattern: /\(bkz-baslik:\s*(.*?)\|\|([^)]+)\)/g, keyPrefix: "bkz-baslik" },
];

function renderPreview(text: string): React.ReactNode[] {
  interface Span { index: number; length: number; node: React.ReactNode; }
  const spans: Span[] = [];
  // Same image-token line-break cleanup as EntryRow's renderContent.
  text = normalizeImageTokenSpacing(text);
  // Images of this draft — shared with every EntryImage so the lightbox can
  // navigate between them.
  const galleryUrls = collectEntryImageUrls(text);
  let m: RegExpExecArray | null;

  PREVIEW_MAC_REF.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = PREVIEW_MAC_REF.exec(text)) !== null)
    spans.push({ index: m.index, length: m[0].length, node: <span key={`mac-${m.index}`} className="epf-preview-ref">{m[1].trim()}</span> });

  PREVIEW_REF_NEW.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = PREVIEW_REF_NEW.exec(text)) !== null)
    spans.push({ index: m.index, length: m[0].length, node: <span key={`ref-${m.index}`} className="epf-preview-ref">{m[1].trim()}</span> });

  PREVIEW_REF_LEGACY.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = PREVIEW_REF_LEGACY.exec(text)) !== null) {
    if (spans.some(s => s.index === m!.index)) continue;
    spans.push({ index: m.index, length: m[0].length, node: <span key={`legacy-${m.index}`} className="epf-preview-ref">{m[2].replace(/--\d+$/, "").replace(/-/g, " ")}</span> });
  }

  PREVIEW_BOLD.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = PREVIEW_BOLD.exec(text)) !== null)
    spans.push({ index: m.index, length: m[0].length, node: <strong key={`b-${m.index}`}>{m[1]}</strong> });

  PREVIEW_ITALIC.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = PREVIEW_ITALIC.exec(text)) !== null)
    spans.push({ index: m.index, length: m[0].length, node: <em key={`i-${m.index}`}>{m[1]}</em> });

  PREVIEW_LINK.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = PREVIEW_LINK.exec(text)) !== null) {
    const url = m[1].trim(); const display = m[2].trim();
    spans.push({ index: m.index, length: m[0].length, node: <span key={`link-${m.index}`} className="epf-preview-link" title={url}>{display} <span className="epf-preview-link-arrow">↗</span></span> });
  }

  PREVIEW_ENTRY_REF.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = PREVIEW_ENTRY_REF.exec(text)) !== null)
    spans.push({ index: m.index, length: m[0].length, node: <span key={`eref-${m.index}`} className="epf-preview-entry-ref">#{m[1]}</span> });

  PREVIEW_PERSON_REF.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = PREVIEW_PERSON_REF.exec(text)) !== null)
    spans.push({ index: m.index, length: m[0].length, node: <span key={`kisi-${m.index}`} className="epf-preview-ref">{m[1].trim()}</span> });

  PREVIEW_CLUB_REF.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = PREVIEW_CLUB_REF.exec(text)) !== null)
    spans.push({ index: m.index, length: m[0].length, node: <span key={`kulup-${m.index}`} className="epf-preview-ref">{m[1].trim()}</span> });

  PREVIEW_TEAM_REF.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = PREVIEW_TEAM_REF.exec(text)) !== null)
    spans.push({ index: m.index, length: m[0].length, node: <span key={`takim-${m.index}`} className="epf-preview-ref">{m[1].trim()}</span> });

  PREVIEW_TOPIC_REF.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = PREVIEW_TOPIC_REF.exec(text)) !== null)
    spans.push({ index: m.index, length: m[0].length, node: <span key={`baslik-${m.index}`} className="epf-preview-ref">{m[1].trim()}</span> });

  // Spoiler block — collapsed by default, click to reveal
  // eslint-disable-next-line no-loop-func
  for (let block = findBlockToken(text, "spoiler", 0); block; block = findBlockToken(text, "spoiler", block.index + block.length)) {
    const hidden = block.content.trim();
    spans.push({ index: block.index, length: block.length,
      node: <SpoilerBlock key={`spoiler-${block.index}`} text={hidden} /> });
  }

  // Quote block — faded background, italic, wrapped in quotes
  // eslint-disable-next-line no-loop-func
  for (let block = findBlockToken(text, "alinti", 0); block; block = findBlockToken(text, "alinti", block.index + block.length)) {
    const quoted = block.content.trim();
    spans.push({ index: block.index, length: block.length,
      node: (
        <blockquote key={`alinti-${block.index}`} className="entry-quote-block">
          {"\u201C"}{renderBasicMarkup(quoted, galleryUrls)}{"\u201D"}
        </blockquote>
      ) });
  }

  // "(bkz: ...)" variants — label styled like inline refs, wrapped in literal parens
  for (const v of PREVIEW_BKZ_REFS) {
    v.pattern.lastIndex = 0;
    // eslint-disable-next-line no-loop-func
    while ((m = v.pattern.exec(text)) !== null) {
      const label = m[1].trim();
      spans.push({ index: m.index, length: m[0].length,
        node: (
          <span key={`${v.keyPrefix}-${m.index}`} className="epf-preview-bkz">
            {"(bkz: "}<span className="epf-preview-ref">{label}</span>{")"}
          </span>
        ) });
    }
  }

  PREVIEW_GORSEL.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = PREVIEW_GORSEL.exec(text)) !== null) {
    const url = m[1].trim();
    spans.push({ index: m.index, length: m[0].length, node: (
      <EntryImage key={`gorsel-${m.index}`} src={url} gallery={galleryUrls} />
    )});
  }

  spans.sort((a, b) => a.index - b.index);
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  for (const span of spans) {
    if (span.index < cursor) continue;
    if (span.index > cursor) nodes.push(text.slice(cursor, span.index));
    nodes.push(span.node);
    cursor = span.index + span.length;
  }
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes;
}

// ─── Topic group types ────────────────────────────────────────────────────────

// Match group — mirrors the original MatchGroup shape
interface MatchGroup {
  type: "match";
  key: string;       // "match-{matchId}"
  label: string;     // "HomeTeam - AwayTeam"
  url: string;
  matchId: number;
  homeTeam: string;
  awayTeam: string;
  matchDate: string;
  drafts: EntryDraft[];
}

// Person group
interface PersonGroup {
  type: "person";
  key: string;       // "person-{personId}"
  label: string;     // person full name
  url: string;
  personId: number;
  drafts: EntryDraft[];
}

// Club group
interface ClubGroup {
  type: "club";
  key: string;       // "club-{clubId}"
  label: string;     // club name
  url: string;
  clubId: number;
  drafts: EntryDraft[];
}

// Team group
interface TeamGroup {
  type: "team";
  key: string;       // "team-{teamId}"
  label: string;     // team name
  url: string;
  teamId: number;
  drafts: EntryDraft[];
}

// Custom topic ("gündem") group
interface CustomTopicGroup {
  type: "customTopic";
  key: string;       // "topic-{customTopicId}"
  label: string;     // custom topic title
  url: string;
  customTopicId: number;
  drafts: EntryDraft[];
}

type TopicGroup = MatchGroup | PersonGroup | ClubGroup | TeamGroup | CustomTopicGroup;

/**
 * Group drafts by their topic (match / person / club / team / custom topic).
 * Returns a flat list of topic groups in the order they were first encountered.
 */
const groupByTopic = (drafts: EntryDraft[]): TopicGroup[] => {
  const map = new Map<string, TopicGroup>();

  for (const draft of drafts) {
    if (draft.matchId != null) {
      const key = `match-${draft.matchId}`;
      if (!map.has(key)) {
        const url = generateMatchUrlFromEntry(
          draft.homeTeam!,
          draft.awayTeam!,
          new Date(draft.matchDate!),
          draft.matchId
        );
        map.set(key, {
          type: "match",
          key,
          label: `${draft.homeTeam} - ${draft.awayTeam}`,
          url,
          matchId: draft.matchId,
          homeTeam: draft.homeTeam!,
          awayTeam: draft.awayTeam!,
          matchDate: draft.matchDate!,
          drafts: [],
        });
      }
      (map.get(key) as MatchGroup).drafts.push(draft);

    } else if (draft.personId != null) {
      const key = `person-${draft.personId}`;
      if (!map.has(key)) {
        map.set(key, {
          type: "person",
          key,
          label: draft.personFullName ?? `Kişi #${draft.personId}`,
          url: `/kisi/${draft.personSlug ?? draft.personId}`,
          personId: draft.personId,
          drafts: [],
        });
      }
      (map.get(key) as PersonGroup).drafts.push(draft);

    } else if (draft.clubId != null) {
      const key = `club-${draft.clubId}`;
      if (!map.has(key)) {
        map.set(key, {
          type: "club",
          key,
          label: draft.clubName ?? `Kulüp #${draft.clubId}`,
          url: `/kulup/${draft.clubSlug ?? draft.clubId}`,
          clubId: draft.clubId,
          drafts: [],
        });
      }
      (map.get(key) as ClubGroup).drafts.push(draft);

    } else if (draft.teamId != null) {
      const key = `team-${draft.teamId}`;
      if (!map.has(key)) {
        // Team URL: /takim/{teamClubSlug}/{teamSlug}
        const url =
          draft.teamClubSlug && draft.teamSlug
            ? `/takim/${draft.teamClubSlug}/${draft.teamSlug}`
            : `/takim/${draft.teamId}`;
        map.set(key, {
          type: "team",
          key,
          label: draft.teamCode
            ? `${draft.teamName ?? `Takım #${draft.teamId}`} ${draft.teamCode.toUpperCase()}`
            : draft.teamName ?? `Takım #${draft.teamId}`,
          url,
          teamId: draft.teamId,
          drafts: [],
        });
      }
      (map.get(key) as TeamGroup).drafts.push(draft);

    } else if (draft.customTopicId != null) {
      const key = `topic-${draft.customTopicId}`;
      if (!map.has(key)) {
        map.set(key, {
          type: "customTopic",
          key,
          label: draft.customTopicTitle ?? `Başlık #${draft.customTopicId}`,
          url: `/gundem/${draft.customTopicSlug ?? draft.customTopicId}`,
          customTopicId: draft.customTopicId,
          drafts: [],
        });
      }
      (map.get(key) as CustomTopicGroup).drafts.push(draft);
    }
  }

  return Array.from(map.values());
};

// ─── DraftsPage ───────────────────────────────────────────────────────────────

export const DraftsPage: React.FC = () => {
  const { t } = useTranslation();
  // Admin-configurable maximum entry length, applied to the draft edit textarea
  const { entryMaxLength } = useSiteSettings();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [drafts, setDrafts] = useState<EntryDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);

  // Accordion state — open by default
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  // Single-delete confirmation (null = none)
  const [singleDeleteConfirmId, setSingleDeleteConfirmId] = useState<number | null>(null);

  // Inline edit state — which draft id is currently being edited and its draft text
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState("");

  // Shared textarea ref for the currently edited draft — reused because only one
  // draft can be in edit mode at a time. Passed to EntryEditorToolbar so that
  // ref/bold/italic/link/image insertions land at the textarea cursor.
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Transient toast for publish failures (rate limit / duplicate) — doesn't block editing
  const [toast, setToast] = useState<string | null>(null);

  // Turkish character keyboard popup — only active during inline edit mode
  const [showTrKeyboard, setShowTrKeyboard] = useState(false);

  // Inline preview toggle — mirrors EntryPostForm behaviour
  const [showPreview, setShowPreview] = useState(false);

  const loadPage = useCallback(async (page: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getDrafts(page, DRAFTS_PAGE_SIZE);
      setDrafts(response.content);
      setCurrentPage(response.currentPage);
      setTotalPages(response.totalPages);
      setTotalItems(response.totalItems);
      setSelectedIds(new Set());
    } catch (err) {
      setError(t("drafts_load_error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadPage(0);
  }, [loadPage]);

  // Hooks always run first — redirect check comes after all hook calls
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // ── Selection helpers ──
  const toggleSelect = (draftId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(draftId)) {
        next.delete(draftId);
      } else {
        next.add(draftId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === drafts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(drafts.map((d) => d.id)));
    }
  };

  // ── Accordion ──
  const toggleGroup = (groupKey: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  // ── Mutations ──
  const handleBulkDelete = async () => {
    setActionLoading(true);
    setShowBulkDeleteConfirm(false);
    try {
      const ids = Array.from(selectedIds);
      await bulkDeleteDrafts(ids);
      setDrafts((prev) => prev.filter((d) => !selectedIds.has(d.id)));
      setTotalItems((prev) => prev - selectedIds.size);
      setSelectedIds(new Set());
    } catch {
      setError(t("drafts_delete_error"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSingleDelete = async (draftId: number) => {
    setSingleDeleteConfirmId(null);
    setActionLoading(true);
    try {
      await deleteDraft(draftId);
      setDrafts((prev) => prev.filter((d) => d.id !== draftId));
      setTotalItems((prev) => prev - 1);
    } catch {
      setError(t("drafts_delete_error"));
    } finally {
      setActionLoading(false);
    }
  };

  // Open inline editor for a draft
  const startEdit = (draft: EntryDraft) => {
    setEditingId(draft.id);
    setEditText(draft.content);
    setShowPreview(false);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText("");
    setShowTrKeyboard(false);
    setShowPreview(false);
  };

  // Save inline edits back to the backend
  const saveEdit = async (draftId: number) => {
    if (!editText.trim()) {
      setToast(t("drafts_edit_empty_error"));
      setTimeout(() => setToast(null), TOAST_DURATION_NORMAL);
      return;
    }
    setActionLoading(true);
    try {
      const updated = await updateDraft(draftId, { content: editText });
      setDrafts((prev) => prev.map((d) => (d.id === draftId ? updated : d)));
      setEditingId(null);
      setEditText("");
      setShowTrKeyboard(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      setToast(parseApiError(msg, t));
      setTimeout(() => setToast(null), TOAST_DURATION_NORMAL);
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Publish a draft → real entry. After success, navigate the user to the
   * topic page of the published entry so they can see it immediately.
   */
  const handlePublish = async (draft: EntryDraft) => {
    setActionLoading(true);
    try {
      const published = await publishDraft(draft.id);
      setDrafts((prev) => prev.filter((d) => d.id !== draft.id));
      setTotalItems((prev) => prev - 1);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(draft.id);
        return next;
      });

      // Navigate to the correct topic page so the newly published entry is visible
      if (draft.matchId != null) {
        const url = generateMatchUrlFromEntry(
          draft.homeTeam!,
          draft.awayTeam!,
          new Date(draft.matchDate!),
          draft.matchId
        );
        navigate(url);
      } else if (draft.personId != null) {
        navigate(`/kisi/${draft.personSlug ?? draft.personId}`);
      } else if (draft.clubId != null) {
        navigate(`/kulup/${draft.clubSlug ?? draft.clubId}`);
      } else if (draft.teamId != null) {
        const url =
          draft.teamClubSlug && draft.teamSlug
            ? `/takim/${draft.teamClubSlug}/${draft.teamSlug}`
            : `/takim/${draft.teamId}`;
        navigate(url);
      } else if (draft.customTopicId != null) {
        navigate(`/gundem/${draft.customTopicSlug ?? draft.customTopicId}`);
      } else if (published) {
        // Fallback — should not happen in practice
        navigate("/");
      }
    } catch (err) {
      // Rate limit or duplicate — keep the draft, show a toast
      if (err instanceof RateLimitError) {
        setToast(t("entry_rate_limit_exceeded"));
      } else if (err instanceof DuplicateEntryError) {
        setToast(t("entry_duplicate_error"));
      } else {
        const msg = err instanceof Error ? err.message : "";
        setToast(parseApiError(msg, t));
      }
      setTimeout(() => setToast(null), TOAST_DURATION_NORMAL);
    } finally {
      setActionLoading(false);
    }
  };

  // Format a date-time ISO string to a readable date (tr-TR)
  const formatDate = (iso: string) => {
    // Shared helper — backend returns naive LocalDateTime strings that are UTC
    const date = parseServerDate(iso);
    if (isNaN(date.getTime())) {
      return iso;
    }
    return date.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const topicGroups = groupByTopic(drafts);

  // ── Render helpers ──

  /**
   * Insert a Turkish character at the current cursor position in the edit textarea.
   * Mirrors the same helper in EntryPostForm.
   */
  const insertTurkishChar = (char: string) => {
    const textarea = editTextareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newText = editText.slice(0, start) + char + editText.slice(end);
    setEditText(newText);
    requestAnimationFrame(() => {
      textarea.selectionStart = start + char.length;
      textarea.selectionEnd = start + char.length;
      textarea.focus();
    });
  };

  /** Render a single draft row (shared across all topic types) */
  const renderDraft = (draft: EntryDraft) => {
    const isEditing = editingId === draft.id;
    return (
      <div
        key={draft.id}
        className={`drafts-entry ${selectedIds.has(draft.id) ? "drafts-entry-selected" : ""}`}
      >
        <div className="drafts-entry-top">
          {drafts.length >= DRAFTS_BULK_MIN && (
            <label
              className="drafts-checkbox-label"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={selectedIds.has(draft.id)}
                onChange={() => toggleSelect(draft.id)}
              />
            </label>
          )}
          <div className="drafts-entry-content">
            {isEditing ? (
              <div className="drafts-edit-wrapper">
                {/* Inline preview — shown above the toolbar when active, mirrors EntryPostForm */}
                {showPreview && editText.trim() && (
                  <div className="epf-preview-inline">
                    <div className="epf-preview-inline-label">Önizleme</div>
                    <div className="epf-preview-body">{renderPreview(editText)}</div>
                  </div>
                )}
                {/* Toolbar — same component used by EntryPostForm, */}
                {/* so ref/bold/italic/link/image popups behave identically */}
                <div className="drafts-edit-toolbar">
                  <EntryEditorToolbar
                    textareaRef={editTextareaRef}
                    content={editText}
                    setContent={setEditText}
                    disabled={actionLoading}
                  />
                  <span className="drafts-edit-char-counter">
                    {editText.length} / {entryMaxLength}
                  </span>
                </div>
                <textarea
                  ref={editTextareaRef}
                  className="drafts-entry-textarea"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  maxLength={entryMaxLength}
                  rows={4}
                />
              </div>
            ) : (
              <span>{draft.content}</span>
            )}
          </div>
        </div>
        <div className="drafts-entry-footer">
          {isEditing ? (
            /* Edit mode footer: keyboard+clear on left, save+cancel on right */
            <>
              <div className="drafts-edit-bottom-left">
                {/* Turkish keyboard toggle */}
                <button
                  type="button"
                  className={`epf-keyboard-btn${showTrKeyboard ? " epf-keyboard-btn--active" : ""}`}
                  title="Türkçe karakter klavyesi"
                  onClick={() => setShowTrKeyboard((prev) => !prev)}
                  disabled={actionLoading}
                >
                  <MdKeyboard size={16} />
                </button>

                {/* Clear textarea button — only when there is content */}
                {editText.length > 0 && (
                  <button
                    type="button"
                    className="epf-clear-btn"
                    title="Temizle"
                    onClick={() => setEditText("")}
                    disabled={actionLoading}
                  >
                    <RiDeleteBin6Line size={16} />
                  </button>
                )}

                {/* Turkish character popup */}
                {showTrKeyboard && (
                  <div className="epf-tr-keyboard">
                    {TURKISH_CHARS.map((char) => (
                      <button
                        key={char}
                        type="button"
                        className="epf-tr-char-btn"
                        onClick={() => insertTurkishChar(char)}
                      >
                        {char}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="drafts-entry-actions">
                <button
                  type="button"
                  className="epf-preview-btn"
                  onClick={() => setShowPreview((prev) => !prev)}
                  disabled={actionLoading || !editText.trim()}
                  title="Önizleme"
                >
                  Önizleme
                </button>
                <button
                  className="drafts-save-btn"
                  onClick={() => saveEdit(draft.id)}
                  disabled={actionLoading}
                  title={t("drafts_save")}
                >
                  <FaSave />
                  {t("drafts_save")}
                </button>
                <button
                  className="drafts-cancel-btn"
                  onClick={cancelEdit}
                  disabled={actionLoading}
                  title={t("drafts_cancel")}
                >
                  <FaTimes />
                  {t("drafts_cancel")}
                </button>
              </div>
            </>
          ) : (
            /* View mode footer: date on left, action buttons on right */
            <>
              <span className="drafts-entry-date">
                {t("drafts_updated_at")}: {formatDate(draft.updatedAt)}
              </span>
              <div className="drafts-entry-actions">
                <button
                  className="drafts-publish-btn"
                  onClick={() => handlePublish(draft)}
                  disabled={actionLoading}
                  title={t("drafts_publish")}
                >
                  <FaPaperPlane />
                  {t("drafts_publish")}
                </button>
                <button
                  className="drafts-edit-btn"
                  onClick={() => startEdit(draft)}
                  disabled={actionLoading}
                  title={t("drafts_edit")}
                >
                  <FaRegEdit />
                  {t("drafts_edit")}
                </button>
                <button
                  className="drafts-delete-btn"
                  onClick={() => setSingleDeleteConfirmId(draft.id)}
                  disabled={actionLoading}
                  title={t("drafts_delete")}
                >
                  <FaTrash />
                  {t("drafts_delete")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="drafts-container">
      {/* Page header */}
      <div className="drafts-header">
        <div className="drafts-header-title">
          <FaRegFileAlt className="drafts-header-icon" />
          <h1>{t("drafts_title")}</h1>
        </div>
        {totalItems > 0 && (
          <div className="drafts-header-actions">
            <span className="drafts-count">
              {t("drafts_count", { count: totalItems })}
            </span>
          </div>
        )}
      </div>

      <div className="drafts-divider" />

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="drafts-bulk-bar">
          <span className="drafts-bulk-count">
            {t("drafts_selected_count", { count: selectedIds.size })}
          </span>
          <div className="drafts-bulk-actions">
            <button
              className="drafts-delete-btn"
              onClick={() => setShowBulkDeleteConfirm(true)}
              disabled={actionLoading}
            >
              <FaTrash />
              {t("drafts_delete_selected")}
            </button>
            <button
              className="drafts-deselect-btn"
              onClick={() => setSelectedIds(new Set())}
              disabled={actionLoading}
            >
              <FaTimes />
              {t("drafts_deselect_all")}
            </button>
          </div>
        </div>
      )}

      {/* Error banner */}
      {error && <p className="drafts-error">{error}</p>}

      {/* Toast */}
      {toast && <div className="drafts-toast">{toast}</div>}

      {/* Loading / empty / list */}
      {loading ? (
        <p className="drafts-placeholder">{t("loading")}</p>
      ) : drafts.length === 0 ? (
        <div className="drafts-empty-state">
          <FaRegFileAlt className="drafts-empty-icon" />
          <p>{t("drafts_empty_state")}</p>
        </div>
      ) : (
        <div className="drafts-groups">
          {/* Select-all row */}
          {drafts.length >= DRAFTS_BULK_MIN && (
            <div className="drafts-select-all-row">
              <label className="drafts-checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedIds.size === drafts.length && drafts.length > 0}
                  onChange={toggleSelectAll}
                />
                {selectedIds.size === drafts.length && drafts.length > 0
                  ? t("drafts_deselect_all")
                  : t("drafts_select_all")}
              </label>
            </div>
          )}

          {topicGroups.map((group) => {
            const isCollapsed = collapsedGroups.has(group.key);
            return (
              <div key={group.key} className="drafts-group">
                {/* Group header */}
                <button
                  className="drafts-group-header"
                  onClick={() => toggleGroup(group.key)}
                >
                  <div className="drafts-group-title">
                    <Link
                      to={group.url}
                      className="drafts-match-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {group.label}
                    </Link>
                    <span className="drafts-group-count">
                      ({group.drafts.length})
                    </span>
                  </div>
                  {isCollapsed ? <FaChevronDown /> : <FaChevronUp />}
                </button>

                {/* Group drafts */}
                {!isCollapsed && (
                  <div className="drafts-group-entries">
                    {group.drafts.map((draft) => renderDraft(draft))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage + 1}
          totalPages={totalPages}
          totalItems={totalItems}
          paginate={(page: number) => loadPage(page - 1)}
        />
      )}

      {/* Bulk delete confirmation modal */}
      {showBulkDeleteConfirm && (
        <ConfirmModal
          message={t("drafts_bulk_delete_confirm")}
          confirmLabel={t("drafts_confirm_yes")}
          cancelLabel={t("drafts_confirm_no")}
          onConfirm={handleBulkDelete}
          onCancel={() => setShowBulkDeleteConfirm(false)}
          disabled={actionLoading}
        />
      )}

      {/* Single delete confirmation modal */}
      {singleDeleteConfirmId !== null && (
        <ConfirmModal
          message={t("drafts_delete_confirm")}
          confirmLabel={t("drafts_confirm_yes")}
          cancelLabel={t("drafts_confirm_no")}
          onConfirm={() => handleSingleDelete(singleDeleteConfirmId)}
          onCancel={() => setSingleDeleteConfirmId(null)}
          disabled={actionLoading}
        />
      )}
    </div>
  );
};